import { BranchableLeaf, Leaf, LinkedLeaf } from './types';
import { CanvasState } from '../../tabs/CampaignWorkflowTab/components/Canvas/state/CanvasState';
import { CanvasEdge, CanvasNode } from '../../tabs/CampaignWorkflowTab/components/Canvas/Canvas';
import { NodeIds, NodeTypeKeys } from './utils/NodeTypeKeys';
import { EdgeTypeKeys } from './utils/EdgeTypes';
import { TriggerNode } from './Entities/Nodes/TriggerNode';
import { DropzoneNode } from './Entities/Nodes/DropzoneNode';
import { EndOfFlowNode } from './Entities/Nodes/EndOfFlowNode';
import { uuid } from 'utils/uuid';
import { StateToNodesMapper } from './utils/StateToNodesMapper';
import { MarkerType } from 'reactflow';
import { theme } from '@salecycle/ui-component-library';
import { BranchNameNode } from './Entities/Nodes/BranchNameNode';

const nodeTypeToText = {
  [NodeTypeKeys.triggerNodeType]: 'trigger',
  [NodeTypeKeys.dropzoneNodeType]: 'dropzone',
  [NodeTypeKeys.endOfFlowNodeType]: 'end of flow',
  [NodeTypeKeys.displayWebContentNodeType]: 'display web content',
  [NodeTypeKeys.sendEmailNodeType]: 'send email',
  [NodeTypeKeys.branchControlNodeType]: 'branch configuration',
  [NodeTypeKeys.branchNameNodeType]: 'branch settings',
};

export class Canvas {
  public static createForNewCampaign() {
    const canvas = new Canvas();

    canvas.isTriggerInfoShown = true;
    canvas.root = new TriggerNode()
      .setNextNode(new DropzoneNode())
      .setNextNode(new EndOfFlowNode(uuid()))
      .hideNodes()
      .removeAllAllowedNodeTypes();

    return canvas;
  }

  public static createDefaultFlow() {
    const canvas = new Canvas();

    canvas.isTriggerInfoShown = false;
    canvas.root = new TriggerNode().setNextNode(new DropzoneNode()).setNextNode(new EndOfFlowNode(uuid()));

    return canvas;
  }

  public static createEmptyFlow(factory: (canvas: Canvas) => Leaf) {
    const canvas = new Canvas();

    canvas.root = factory(canvas);

    return canvas;
  }

  public static createFromState(state: CanvasState) {
    const canvas = new Canvas();
    const root = new StateToNodesMapper(state).convert();

    canvas.root = root;
    canvas.isTriggerInfoShown = state.showIntroVariant;

    return canvas;
  }

  private root: LinkedLeaf;

  public isTriggerInfoShown: boolean;

  public hideTriggerIntro() {
    this.isTriggerInfoShown = false;
    this.root.showNodes();

    return this;
  }

  private replaceDropzone(node: Leaf) {
    // Need to re-set the allowed node list. In order to achieve this a new Trigger node
    // is created, but this time with the default list of allowed nodes.
    this.root = new TriggerNode(undefined, this.root.position, this.root.getNextNode());
    this.root.replaceNode(node, NodeIds.dropzone);

    return this;
  }

  private addBranchControlNode(node: Leaf, addToId: string) {
    if (node.type !== NodeTypeKeys.branchControlNodeType) {
      return;
    }

    const inheritedNodes = this.root.getNode(addToId).getNextNode();
    this.initialiseBranchControl(node as BranchableLeaf, inheritedNodes);
  }

  public add(node: Leaf, addToId: string) {
    this.addBranchControlNode(node, addToId);

    if (addToId === NodeIds.dropzone) {
      this.replaceDropzone(node);
      return this;
    }

    if (addToId === NodeIds.dropNode) {
      const branchNode = this.getAllNodes().find(s => s.type === NodeTypeKeys.branchControlNodeType) as BranchableLeaf;
      if (branchNode) {
        branchNode.hidePlaceHolderDropNodeFor(node, addToId);
      } else {
        this.root.replaceNode(node, addToId);
      }

      return this;
    }

    this.root.addNode(node, addToId);

    return this;
  }

  public addNext(node: Leaf) {
    this.root.setNextNode(node);
    return this;
  }

  private replaceLastMessageNodeWithDropzone(node: Leaf) {
    this.root.replaceAllowedNodeTypes([NodeTypeKeys.dropzoneNodeType]);
    this.root.replaceNode(new DropzoneNode(), node?.id);
  }

  public removeNode(nodeId: string) {
    const hasOtherMessagesInFlow =
      this.root.getNodes().filter(s => s.type !== NodeTypeKeys.endOfFlowNodeType && s.id !== NodeIds.trigger).length >
      1;

    const branchNode = this.getAllNodes().find(s => s.type === NodeTypeKeys.branchControlNodeType) as BranchableLeaf;

    if (branchNode) {
      branchNode.removeNode(nodeId);
    } else if (hasOtherMessagesInFlow) {
      this.root.removeNode(nodeId);
    } else {
      const nodeToRemove = this.root.getNode(nodeId);
      this.replaceLastMessageNodeWithDropzone(nodeToRemove);
    }

    return this;
  }

  public showDropNodesFor(nodeType: string) {
    if (this.root.getNodes().find(s => s.type === NodeTypeKeys.dropzoneNodeType)) {
      return this;
    }

    this.root.showDropNodeFor(nodeType);

    return this;
  }

  public hideDropNodesFor(nodeType: string) {
    this.root.hideDropNodeFor(nodeType);
    return this;
  }

  public createBranch(branchNodeId: string) {
    const branchNode = this.root.getNode(branchNodeId) as BranchableLeaf;

    branchNode?.createBranch();

    return this;
  }

  public removeBranch(branchNodeId: string) {
    const branchNode = this.getAllNodes().find(s => s.type === NodeTypeKeys.branchControlNodeType) as BranchableLeaf;

    branchNode?.deleteBranch(branchNodeId, this.root);

    return this;
  }

  public removeBranchControlNode() {
    const branchNode = this.getAllNodes().find(s => s.type === NodeTypeKeys.branchControlNodeType) as BranchableLeaf;
    branchNode.deleteBranchControlNode(this.root);

    return this;
  }

  public reOrderBranches(id: string, ids: string[]) {
    const controlNode = this.root.getNode(id) as BranchableLeaf;
    controlNode.reOrderBranches(ids);

    return this;
  }

  private initialiseBranchControl(branchNode: BranchableLeaf, inheritedNode?: Leaf) {
    branchNode?.createBranch(inheritedNode, !!inheritedNode);
    branchNode?.createBranch();

    return this;
  }

  public enableSplitTest(displayWebContentId: string, segmentId: string) {
    const node = this.root.getNode(displayWebContentId);

    node.id = segmentId;

    return this;
  }

  public disableSplitTest(splitTestId: string, newMessageId: string) {
    const node = this.root.getNode(splitTestId);

    node.id = newMessageId;

    return this;
  }

  public hasBranchNodes() {
    return !!this.getAllNodes().find(s => s.type === NodeTypeKeys.branchControlNodeType);
  }

  private getAllNodes() {
    const get = (nodes: Leaf[]) => {
      const branchNode = nodes.find(s => s.type === NodeTypeKeys.branchControlNodeType);
      if (branchNode) {
        const nodesFromBranch = get((branchNode as BranchableLeaf).getAllNodes());
        nodes = nodes.concat(nodesFromBranch);
      }

      return nodes;
    };

    return get(this.root.getNodes());
  }

  public hasAvailableDropNodes(nodeType: string) {
    if (this.root.getNodes().find(s => s.type === NodeTypeKeys.dropzoneNodeType)) {
      return true;
    }

    this.root.showDropNodeFor(nodeType);

    return this.root.getNodes().some(s => s.getIsDropNodeShown());
  }

  public hasNodeInFlow(nodeType: string): boolean {
    return !!this.getAllNodes().find(s => s.type === nodeType);
  }

  public convertToState(): CanvasState {
    const createEdges = (nodes: Leaf[], source: any) => {
      let edges: CanvasEdge[] = [];

      nodes.forEach(currentNode => {
        if (!source) {
          source = currentNode;
          return;
        }

        if (currentNode.type === NodeTypeKeys.branchControlNodeType) {
          (currentNode as BranchableLeaf).getChildren().forEach(branch => {
            const edgesFromBranches = createEdges(branch.getNodes(), currentNode);
            edges = edges.concat(edgesFromBranches);
          });
        }

        const sourceNodeId = source.id;
        edges.push({
          id: `${sourceNodeId}->${currentNode.id}`,
          source: sourceNodeId,
          target: currentNode.id,
          hidden: currentNode.isHidden(),
          markerEnd:
            source.type !== NodeTypeKeys.branchControlNodeType
              ? {
                  type: MarkerType.Arrow,
                  width: 15,
                  height: 15,
                  color: theme.designToken.neutral500,
                }
              : undefined,
          data: {
            allowedNodes: source.getAllowedNodeTypes(),
            connectorButtonProps: {
              show: source.getIsDropNodeShown(),
              droppableProps: { id: source.id, 'aria-label': `${nodeTypeToText[source.type]} drop area` },
            },
          },
          type: EdgeTypeKeys.floating,
        });

        source = currentNode;
      });

      return edges;
    };

    const createNodes = (): CanvasNode[] => {
      const nodes = this.getAllNodes().reduce((acc, s, index, array) => {
        const node: CanvasNode = {
          id: s.id,
          hidden: s.isHidden(),
          data: {},
          type: s.type,
          position: s.position,
        };

        if (node.type === NodeTypeKeys.branchControlNodeType) {
          node.data.children = (s as BranchableLeaf).getChildren().flatMap(item => item.id);
          node.data.restrictToType = (s as BranchableLeaf).getTypeToConfigureFor();
        } else if (node.type === NodeTypeKeys.branchNameNodeType) {
          node.data.parentId = s.id;
          // Order isn't used for anything specific. It's provided as a way to force
          // the canvas to re-render when the branches have been re-arranged.
          node.data.order = (s as BranchNameNode).order;
        } else if (index > 0) {
          const previous = array[index - 1];
          node.data.parentId = previous?.id;
        } else {
          node.data.parentId = undefined;
        }

        acc.push(node);

        return acc;
      }, []);
      return nodes;
    };

    return {
      nodes: createNodes(),
      edges: createEdges(this.root.getNodes(), undefined),
      showIntroVariant: this.isTriggerInfoShown,
    };
  }
}
